import { useQuery } from '@tanstack/react-query';
import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import { downloadFileBlob } from './downloadFileBlob';

interface ProgrammeDocumentDownloadResponse {
	payload: {
		downloadUrl: string;
	};
}

export const PROGRAMME_SIGNING_DOCUMENT_QUERY_KEY = (token: string, signatureId: string) => [
	'programme-signing-document',
	token,
	signatureId,
];

/**
 * Fetches the viewable document blob for one of the authorised signatory's own
 * programme signatures: asks the backend for a pre-signed download URL
 * (ownership-checked against the programme token — see backend
 * `PublicBulkSigningController#getProgrammeDocument`), then downloads the file
 * itself. Mirrors the single-doc `usePublicSigningDocument` two-step fetch.
 *
 * Pass an empty `signatureId` to keep the query idle (no preview open).
 */
export const useProgrammeSigningDocumentPreview = (token: string, signatureId: string) =>
	useQuery({
		queryKey: PROGRAMME_SIGNING_DOCUMENT_QUERY_KEY(token, signatureId),
		queryFn: async () => {
			const response = await makeRequest<ProgrammeDocumentDownloadResponse>(
				{
					endpoint: '/public-signing/programme/document',
					method: HTTP_METHOD.GET,
					queryString: { token, signatureId },
				},
				// Unauthenticated flow: a 401 (e.g. expired programme token) must
				// surface as a query error, not redirect to the login page.
				{ skipLoginRedirect: true }
			);

			return downloadFileBlob(response.payload.downloadUrl);
		},
		enabled: !!token && !!signatureId,
		retry: false,
		staleTime: Infinity,
	});
